import {
  CompanyListSearchFormSchema,
  type CompanyListSearchFormSchemaType,
} from "@/pages/company-list/model/schema";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseCheckboxGroup } from "@/shared/ui/base-checkbox-group";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Props = {
  onSearch: (searchParams: CompanyListSearchFormSchemaType) => void;
  setFilterCount: (count: number) => void;
};

const initialValues: CompanyListSearchFormSchemaType = {
  companyName: "",
  address: "",
  companyCategories: [],
};

export const SearchForm = ({ onSearch, setFilterCount }: Props) => {
  const { t } = useTranslation();

  const form = useForm<CompanyListSearchFormSchemaType>({
    resolver: zodResolver(CompanyListSearchFormSchema),
    defaultValues: initialValues,
  });

  const onSubmit = (data: CompanyListSearchFormSchemaType) => {
    onSearch(data);

    // フォーム送信のタイミングでフィルター数を更新する
    let count = 0;
    if (data.companyName) count++;
    if (data.address) count++;
    if (data.companyCategories && data.companyCategories.length > 0) count++;
    setFilterCount(count);
  };

  const onReset = () => {
    onSearch(initialValues);
    form.reset(initialValues);
    setFilterCount(0);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Stack
        p={3}
        spacing={3}
        borderRadius={"18px"}
        boxShadow="0px 1px 10px 0px #0000001A"
      >
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <BaseFormLabel label="会社名">
            <Controller
              name="companyName"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="会社名を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="住所">
            <Controller
              name="address"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="住所を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>
        </Stack>

        <BaseFormLabel label="企業種別">
          <Controller
            name="companyCategories"
            control={form.control}
            render={({ field, fieldState }) => (
              <BaseCheckboxGroup
                options={["shipper", "carrier"].map((item) => ({
                  key: item,
                  label: t(item as "shipper" | "carrier"),
                }))}
                selectedValues={field.value ?? []}
                onChange={field.onChange}
                error={fieldState.invalid}
              />
            )}
          />
        </BaseFormLabel>

        <Stack direction="row" spacing={3} mt={1}>
          <BaseButton variant="contained" type="submit">
            絞り込む
          </BaseButton>
          <BaseButton variant="outlined" type="button" onClick={onReset}>
            リセット
          </BaseButton>
        </Stack>
      </Stack>
    </form>
  );
};
